import { useCallback, useEffect, useState } from "react";
import PageTitle from "../components/PageTitle";
import { PARTNERS_DEFAULT } from "../data/partnersDefault";
import {
  fetchPartnersForAdmin,
  savePartners,
  uploadPartnerImage,
} from "../api/opsApi";

// Editing the band of partner logos along the foot of the home page: who is on it, where each
// badge links to, how big it is drawn and in what order.
//
// The whole band is held in one piece of state and saved in one go, the same way the fleet
// page is edited. Gated by the portal session and the administrator role on the server as well
// as here.

const copyOf = (partners) => partners.map((partner) => ({ ...partner }));

export default function PartnersAdminPage() {
  const [partners, setPartners] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const document = await fetchPartnersForAdmin();
      // Nothing saved yet: start from what the page is showing today.
      setPartners(copyOf(document.partners?.length ? document.partners : PARTNERS_DEFAULT));
      setSavedAt(document.updatedAt ?? null);
      setDenied(false);
      setDirty(false);
    } catch (error) {
      if (error.status === 401 || error.status === 403) setDenied(true);
      else setMessage({ bad: true, text: error.message });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // Every edit goes through here, so nothing can change the band without marking it unsaved.
  const edit = (change) => {
    setPartners((current) => {
      const next = copyOf(current);
      change(next);
      return next;
    });
    setDirty(true);
    setMessage(null);
  };

  const move = (index, by) => {
    const to = index + by;
    if (to < 0 || to >= partners.length) return;
    edit((next) => {
      const [row] = next.splice(index, 1);
      next.splice(to, 0, row);
    });
  };

  const addLogo = async (index, file) => {
    if (!file) return;
    setBusy(true);
    setMessage(null);
    try {
      const image = await uploadPartnerImage(file);
      edit((next) => {
        next[index].image = image.url;
      });
      setMessage({ text: "Logo uploaded. Save to publish it." });
    } catch (error) {
      setMessage({ bad: true, text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await savePartners(partners);
      setSavedAt(new Date().toISOString());
      setDirty(false);
      setMessage({
        text: [
          "Saved.",
          result.removed ? `${result.removed} unused logo${result.removed === 1 ? "" : "s"} removed.` : null,
        ]
          .filter(Boolean)
          .join(" "),
      });
    } catch (error) {
      setMessage({ bad: true, text: error.message });
    } finally {
      setBusy(false);
    }
  };

  if (denied) {
    return (
      <>
        <PageTitle>vWZZ - Partners admin</PageTitle>
        <div className="page-container shots-admin">
          <h1 className="page-title">Partners admin</h1>
          <p className="shots-empty">
            Sign in with an administrator account to edit the partners.{" "}
            <a href="/interep">Go to sign in</a>
          </p>
        </div>
      </>
    );
  }

  if (!partners) {
    return (
      <>
        <PageTitle>vWZZ - Partners admin</PageTitle>
        <div className="page-container shots-admin">
          <h1 className="page-title">Partners admin</h1>
          <p className="shots-empty">Loading...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle>vWZZ - Partners admin</PageTitle>
      <div className="page-container shots-admin fleet-admin">
        <header className="shots-header">
          <h1 className="page-title">Partners admin</h1>
          <p className="page-subtitle">
            The logos along the foot of the home page: who is on the band, where each one links
            to and how big it is drawn.
          </p>
        </header>

        <div className="fleet-admin-bar">
          <span>
            {savedAt ? `Last saved ${new Date(savedAt).toLocaleString("en-GB")}` : "Not saved yet"}
            {dirty ? " · unsaved changes" : ""}
          </span>
          <div className="shots-form-actions">
            {message ? (
              <span className={`shots-message ${message.bad ? "is-bad" : ""}`} role="status">
                {message.text}
              </span>
            ) : null}
            <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !dirty}>
              {busy ? "Working..." : "Save partners"}
            </button>
          </div>
        </div>

        <section className="fleet-admin-block">
          <h2>The band</h2>
          <p className="shots-note">
            Shown left to right in this order. A partner with no link is still a badge, just not
            a clickable one. Every logo is drawn in a box of the same size, and the empty
            transparent border is cropped off a PNG as it is uploaded, so they all come out the
            same weight without anything to set by hand.
          </p>

          <ol className="partner-admin-list">
            {partners.map((partner, index) => (
              <li key={`${index}-${partner.image}`}>
                {/* Drawn in the band's own box, so the editor shows what the page will. */}
                <div className="partner-admin-logo">
                  <img className="logo" src={partner.image} alt={partner.name || "Partner logo"} />
                </div>

                <div className="partner-admin-fields">
                  <label>
                    <span>Name</span>
                    <input
                      type="text"
                      maxLength={80}
                      value={partner.name}
                      onChange={(event) => edit((next) => { next[index].name = event.target.value; })}
                    />
                  </label>

                  <label>
                    <span>Links to</span>
                    <input
                      type="url"
                      maxLength={300}
                      placeholder="https://example.com"
                      value={partner.href ?? ""}
                      onChange={(event) => edit((next) => { next[index].href = event.target.value; })}
                    />
                  </label>

                  <label className="partner-admin-file">
                    <span>Replace the logo</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      disabled={busy}
                      onChange={(event) => {
                        addLogo(index, event.target.files?.[0]);
                        event.target.value = "";
                      }}
                    />
                  </label>
                </div>

                <div className="partner-admin-actions">
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move left">
                    &uarr;
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === partners.length - 1}
                    aria-label="Move right"
                  >
                    &darr;
                  </button>
                  <button
                    type="button"
                    className="is-remove"
                    onClick={() => edit((next) => { next.splice(index, 1); })}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ol>

          {partners.length === 0 ? <p className="shots-empty">No partners on the band.</p> : null}

          <label className="partner-admin-add">
            <span className="btn btn-secondary">Add a partner</span>
            <input
              type="file"
              accept="image/png,image/jpeg"
              disabled={busy}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                setBusy(true);
                setMessage(null);
                try {
                  const image = await uploadPartnerImage(file);
                  edit((next) => {
                    next.push({ name: "", image: image.url, href: "" });
                  });
                  setMessage({ text: "Logo uploaded. Give it a name, then save." });
                } catch (error) {
                  setMessage({ bad: true, text: error.message });
                } finally {
                  setBusy(false);
                }
              }}
            />
          </label>
        </section>
      </div>
    </>
  );
}
